import React, { useState } from "react";
import styled from "styled-components";
import { Link, useNavigate } from "react-router-dom";
import { useCustomAlertStore } from "../Store/store";
import {
  Main,
  Container,
  Header,
  Form,
  InputGroup,
  ErrorMessage,
  Button,
  LoginLink,
  LoadingSpinner,
} from "./RegisterPage/RegisterPage.styles";
import { requestCompany } from "../api/userManagementAPI";

const BackButton = styled.button`
  position: absolute;
  top: 20px;
  left: 20px;
  background: rgba(255, 255, 255, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.15);
  border-radius: 8px;
  color: #fff;
  padding: 10px 15px;
  cursor: pointer;
  font-size: 14px;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  gap: 5px;

  &:hover {
    background: rgba(255, 255, 255, 0.15);
    transform: translateX(-2px);
  }

  &:before {
    content: "←";
    font-size: 16px;
  }
`;

export default function CompanyRequestPage() {
  const navigate = useNavigate();
  const { showSuccessAlert, showErrorAlert } = useCustomAlertStore();

  const [formData, setFormData] = useState({
    companyName: "",
    businessNumber: "",
    companyNumber: "",
    companyEmail: "",
    companyAddress: "",
    job: "",
  });
  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);

  // 사업자번호 포맷팅
  const formatBusinessNumber = (value) => {
    const numbers = value.replace(/[^\d]/g, "");

    if (numbers.length <= 3) return numbers;
    if (numbers.length <= 5)
      return `${numbers.slice(0, 3)}-${numbers.slice(3)}`;
    return `${numbers.slice(0, 3)}-${numbers.slice(3, 5)}-${numbers.slice(
      5,
      10,
    )}`;
  };

  // 폼 검증
  const validateForm = () => {
    const newErrors = {};

    if (!formData.companyName.trim()) {
      newErrors.companyName = "회사명을 입력해주세요.";
    }

    if (!formData.businessNumber.trim()) {
      newErrors.businessNumber = "사업자번호를 입력해주세요.";
    } else if (formData.businessNumber.replace(/[^\d]/g, "").length !== 10) {
      newErrors.businessNumber = "올바른 사업자번호를 입력해주세요. (10자리)";
    }

    if (!formData.companyNumber.trim()) {
      newErrors.companyNumber = "회사 전화번호를 입력해주세요.";
    }

    if (!formData.companyEmail.trim()) {
      newErrors.companyEmail = "회사 이메일을 입력해주세요.";
    } else if (!/\S+@\S+\.\S+/.test(formData.companyEmail)) {
      newErrors.companyEmail = "올바른 이메일 형식을 입력해주세요.";
    }

    if (!formData.companyAddress.trim()) {
      newErrors.companyAddress = "회사 주소를 입력해주세요.";
    }

    if (!formData.job.trim()) {
      newErrors.job = "회사 업종을 입력해주세요.";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // 입력 핸들러
  const handleInputChange = (e) => {
    const { name, value } = e.target;

    let processedValue = value;

    // 사업자번호 포맷팅
    if (name === "businessNumber") {
      processedValue = formatBusinessNumber(value);
    }

    setFormData((prev) => ({
      ...prev,
      [name]: processedValue,
    }));

    // 실시간 에러 제거
    if (errors[name]) {
      setErrors((prev) => ({
        ...prev,
        [name]: "",
      }));
    }
  };

  // 제출 핸들러
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setIsLoading(true);

    try {
      // TODO: API 호출 로직 추가
      const response = await requestCompany(formData);

      // 임시로 2초 후 성공 처리
      setTimeout(() => {
        setIsLoading(false);
        showSuccessAlert(
          "기관/기업명 추가 요청 완료",
          "기관/기업명 추가 요청이 완료되었습니다. 검토 후 연락드리겠습니다.",
        );
        navigate("/login");
      }, 2000);
    } catch (error) {
      console.error("기관/기업명 추가 요청 실패:", error);
      showErrorAlert(
        "요청 실패",
        "기관/기업명 추가 요청에 실패했습니다. 다시 시도해주세요.",
      );
      setIsLoading(false);
    }
  };

  const handleBackClick = () => {
    navigate(-1);
  };

  const isFormValid =
    Object.values(formData).every((value) => value.trim() !== "") &&
    Object.keys(errors).length === 0;

  return (
    <Main>
      <BackButton onClick={handleBackClick}>뒤로가기</BackButton>

      <Container>
        <Header className="project-header">
          <h1>기관/기업명 추가 요청</h1>
          <p>등록되지 않은 기관/기업명을 추가 요청할 수 있습니다.</p>
        </Header>

        <Form onSubmit={handleSubmit}>
          <InputGroup error={errors.companyName}>
            <label htmlFor="companyName">회사명 *</label>
            <input
              type="text"
              id="companyName"
              name="companyName"
              value={formData.companyName}
              onChange={handleInputChange}
              placeholder="회사명을 입력해주세요"
              disabled={isLoading}
            />
            {errors.companyName && (
              <ErrorMessage>{errors.companyName}</ErrorMessage>
            )}
          </InputGroup>

          <InputGroup error={errors.businessNumber}>
            <label htmlFor="businessNumber">사업자번호 *</label>
            <input
              type="text"
              id="businessNumber"
              name="businessNumber"
              value={formData.businessNumber}
              onChange={handleInputChange}
              placeholder="000-00-00000"
              maxLength={12}
              disabled={isLoading}
            />
            {errors.businessNumber && (
              <ErrorMessage>{errors.businessNumber}</ErrorMessage>
            )}
          </InputGroup>

          <InputGroup error={errors.companyNumber}>
            <label htmlFor="companyNumber">회사 전화번호 *</label>
            <input
              type="tel"
              id="companyNumber"
              name="companyNumber"
              value={formData.companyNumber}
              onChange={handleInputChange}
              placeholder="02-0000-0000"
              maxLength={20}
              disabled={isLoading}
            />
            {errors.companyNumber && (
              <ErrorMessage>{errors.companyNumber}</ErrorMessage>
            )}
          </InputGroup>

          <InputGroup error={errors.companyEmail}>
            <label htmlFor="companyEmail">회사 이메일 *</label>
            <input
              type="email"
              id="companyEmail"
              name="companyEmail"
              value={formData.companyEmail}
              onChange={handleInputChange}
              placeholder="company@example.com"
              disabled={isLoading}
            />
            {errors.companyEmail && (
              <ErrorMessage>{errors.companyEmail}</ErrorMessage>
            )}
          </InputGroup>

          <InputGroup error={errors.companyAddress}>
            <label htmlFor="companyAddress">회사 주소 *</label>
            <input
              type="text"
              id="companyAddress"
              name="companyAddress"
              value={formData.companyAddress}
              onChange={handleInputChange}
              placeholder="서울특별시 강남구 테헤란로 000"
              disabled={isLoading}
            />
            {errors.companyAddress && (
              <ErrorMessage>{errors.companyAddress}</ErrorMessage>
            )}
          </InputGroup>

          <InputGroup error={errors.job}>
            <label htmlFor="job">회사 업종 *</label>
            <input
              type="text"
              id="job"
              name="job"
              value={formData.job}
              onChange={handleInputChange}
              placeholder="예: 농업, 제조업, IT서비스업"
              disabled={isLoading}
            />
            {errors.job && <ErrorMessage>{errors.job}</ErrorMessage>}
          </InputGroup>

          <Button type="submit" disabled={!isFormValid || isLoading}>
            {isLoading ? (
              <>
                <LoadingSpinner /> 요청중...
              </>
            ) : (
              "등록"
            )}
          </Button>
        </Form>

        <LoginLink>
          <Link to="/login">로그인 페이지로 돌아가기</Link>
        </LoginLink>
      </Container>
    </Main>
  );
}
